/**
 * russian-accent.js — automatic Russian stress (ударение) + ё restoration.
 *
 * Marks the stressed vowel with the combining acute U+0301 (which Supertonic's
 * tokenizer accepts) so the TTS reads correct stress. Dictionary is built from
 * OpenRussian/Wiktionary data (CC-BY-SA, see vendor/ru-accent/LICENSE).
 *
 *   535k+ unambiguous wordforms → marked exactly.
 *   ~6.5k homographs (за́мок/замо́к, го́лоса/голоса́) → left UNMARKED by default
 *   (Supertonic guesses) — caller can pass {homograph:'first'} or the user can
 *   override manually with '+' before the stressed vowel (handled upstream).
 *
 * Browser:  window.RussianAccent
 * Node:     module.exports (for unit tests)
 *
 * API:
 *   RussianAccent.load({base='/vendor/ru-accent', fetchJson?}) → Promise
 *   RussianAccent.isReady() → bool
 *   RussianAccent.accentuate(text, {homograph='leave'|'first', skipMarked=true}) → string
 *   RussianAccent.lookup(word) → {type:'single'|'homograph'|'miss', forms[, source:'norm'|'yo', src]}
 *   RussianAccent.norm(word) → {form, src} | null — запись слоя нормы (источник: «Орфоэпический список» ФИПИ)
 *   RussianAccent.yoWordForm(word) → форма слова, написанного с «ё», если главное ударение не на ней
 *   RussianAccent.mainVowel(form) → номер гласной главного ударения (правый знак или «ё»), -1 если нет
 */
(function (root, factory) {
    // фабрика получает саму себя: из её текста load() собирает воркер разбора
    // (тот же код в потоке, без второго файла и без расхождения версий)
    var mod = factory(factory);
    if (typeof module !== 'undefined' && module.exports) module.exports = mod;
    if (typeof window !== 'undefined') window.RussianAccent = mod;
})(this, function (SELF) {
    'use strict';

    var ACUTE = '́';                 // combining acute (stress)
    var VOWELS = 'аеёиоуыэюя';
    var CYR = /[А-Яа-яЁё]/;
    var WORD_RE = /[А-Яа-яЁё]+(?:[́̀+][А-Яа-яЁё]*)*|[А-Яа-яЁё́̀+]+/g;

    var SINGLE = null;     // foldedKey -> accented (lowercase, with U+0301, ё kept)
    var HOMO = null;       // foldedKey -> [accented,...]
    // ── имена (2026-09-18) ────────────────────────────────────────────────
    // Отдельный слой, а не строки в SINGLE, по одной причине: имя опознаётся
    // ЗАГЛАВНОЙ БУКВОЙ. «Па́ша» — имя, «паша́» — титул; «По́ля» — имя, «поля́» —
    // множественное от «поле». Замер 18.09 на 44 именах: словарь ставил верно
    // 24, не знал 18 (почти все — уменьшительные: Маша, Даша, Саша, Катя,
    // Оля, Коля, Женя, Наташа, Серёжа…), и ДВА ставил неверно (Паша́, Ва́рвара).
    // NAMES дополняет и не перебивает; NAMES_OVR перебивает, и каждая запись
    // там обоснована в самом файле данных.
    var NAMES = null;      // foldedKey -> accented (дополняет словарь)
    var NAMES_OVR = null;  // foldedKey -> accented (перебивает словарь)
    // ── машинный слой (2026-09-30) ────────────────────────────────────────
    // Ударения акцентора silero-stress (MIT) для обычных форм, которых нет в
    // словаре: деепричастия (говоря́, учи́тывая), краткие причастия (сде́лано),
    // местоимения и числительные (кого́, обо́их, десяти́), сравнительные
    // (побо́льше). До него без ударения оставалось каждое 67-е слово живого
    // текста; на формах, размеченных людьми, акцентор прав в 134 из 135.
    // Это ДОГАДКИ, а не словарь: lookup() их не видит и по-прежнему отвечает
    // «miss», поэтому ручная пометка человека на таком слове идёт в урожай, как
    // шла. accentuate() берёт догадку последней — после словаря, curated,
    // урожая и имён. Сборка: services/silero-tts/build_accentor_layer.py.
    var GUESS = null;      // foldedKey -> accented
    // ── норма (2026-09-30) ────────────────────────────────────────────────
    // Слой, который ПЕРЕБИВАЕТ словарь и все остальные слои: у каждой записи источник
    // (список ФИПИ), и она исправляет то, в чём CC-BY-SA словарь расходится с
    // нормой: «банты́» → «ба́нты», «позва́ла» → «позвала́», ложный омограф
    // «при́даное / прида́ное» → «прида́ное». Отдельно от SINGLE, чтобы lookup()
    // мог сказать, ОТКУДА ответ (школьная страница показывает источник), а
    // слой урожая и имена не могли спорить с нормой. Файл —
    // /data/lang/ru-stress-norm.json, тот же читает services/silero-tts/accent.py.
    var NORM = null;       // foldedKey -> {form, src}
    // ── ё (2026-10-01) ────────────────────────────────────────────────────
    // Слов с ударной «ё» словарь ударений почти не хранит (ё ударна сама), а
    // люди пишут без «ё». До 01.10.2026 этот лейн не знал «еще», «ее», «идет»,
    // «пришел», «серьезно» — 1.58 % живого текста (частотный список
    // OpenSubtitles) без ударения, — тогда как сервер восстанавливал ё тем же
    // словарём eyo и читал их верно. Теперь lookup() спрашивает тот же
    // «безопасный» словарь eyo (/vendor/eyo/bundle.safe.js; у сервера — его
    // выгрузка ru-yo-safe.json.gz) по одному правилу для обоих лейнов:
    //   словарь ударений слова не знает            → ё-форма («идёт»);
    //   знает то же ё (и главное ударение дальше)  → словарь («трёхэта́жный»);
    //   ставит ударение на ту же гласную           → ё-форма («беготнёй»).
    // Где eyo и словарь спорят, решено заранее: /data/lang/ru-stress-yo.json
    // (сборка services/silero-tts/build_yo_stress_layer.py) — `forms` уже
    // лежат в SINGLE/HOMO, `skip_yo` — слова, где ё у eyo неверна («галдёжа»).
    var YO = null;         // е-ключ -> ё-форма (словарь eyo, как есть)
    var YO_SKIP = null;    // foldedKey -> 1: eyo здесь не спрашивать
    // «все / всё», «звезды / звёзды», «моем / моём» (2026-10-12): словарь читает
    // их одним чтением без «ё» («все́», «звезды́»), а контекстная модель сервера
    // в предложении — верно чаще (замер и список — /data/lang/ru-stress-yo-model.json,
    // тот же файл читает accent.py). Омографами здесь они не считаются, поэтому
    // contextPicks их не спрашивал, а validPick отбрасывал ответ «всё»: Supertonic
    // говорил «все́ равно», Silero того же текста — «всё равно».
    var YO_CTX = null;     // foldedKey -> 1: спросить сервер, принять его «ё»
    var CAP_RE = /^[А-ЯЁ]/;
    var ready = false;

    function foldKey(s) {
        // strip stress marks (+, U+0301, U+0300), ё->е, lower
        return s.replace(/[+́̀]/g, '').replace(/ё/g, 'е').replace(/Ё/g, 'Е').toLowerCase();
    }

    // A dictionary form may contain nothing but Russian letters, a hyphen and
    // stress marks. The CC-BY-SA source breaks that in exactly three of its
    // 553k values, all with a stray apostrophe — «мя́'тно», «мя́'тны» and the
    // homograph «что» → ["'что́","что́"] — and each one used to reach the
    // textarea verbatim. Cleaned at the two places a dict form becomes text:
    // here (every user-visible form) and at load (homograph lists). 2026-08-07
    var DIRT_RE = /[^А-Яа-яЁё́̀-]/g;
    function cleanForm(f) { return String(f).replace(DIRT_RE, ''); }

    // Apply the lowercase accented dictionary form onto the original token,
    // preserving the original capitalization pattern.
    function applyCase(original, accentedLower) {
        accentedLower = cleanForm(accentedLower);
        // strip original of any existing marks for case comparison
        var origLetters = original.replace(/[+́̀]/g, '');
        if (!origLetters) return accentedLower;
        var firstUpper = origLetters[0] === origLetters[0].toUpperCase() && origLetters[0] !== origLetters[0].toLowerCase();
        var allUpper = origLetters.length > 1 && origLetters === origLetters.toUpperCase();
        if (allUpper) return accentedLower.toUpperCase();
        if (firstUpper) return accentedLower.charAt(0).toUpperCase() + accentedLower.slice(1);
        return accentedLower;
    }

    function countVowels(w) {
        var n = 0;
        for (var i = 0; i < w.length; i++) if (VOWELS.indexOf(w[i].toLowerCase()) >= 0) n++;
        return n;
    }

    // ── Упакованные таблицы (2026-10-16) ─────────────────────────────────
    // Словарь (560 тыс. форм), догадки акцентора и словарь eyo собирает ВОРКЕР
    // (см. load): JSON.parse 25 МБ, TextDecoder, скрипт eyo и наложение слоёв
    // занимали главный поток телефона 3–4 с длинными задачами (замер — в
    // docs/why/primitives/ru-accent-worker.md). Воркер отдаёт готовые таблицы
    // переносом буферов, без копии: ключи и формы — по байту на букву, хеш-таблица
    // с открытой адресацией. Поиск на странице — хеш ключа и сравнение байтов,
    // ни одного объекта на слово. Строка, где есть буква вне алфавита ниже, лежит
    // в `extra` как есть, поэтому ответы те же, что у объекта.
    function encCh(c) {
        if (c < 0x80) return c;
        if (c >= 0x430 && c <= 0x44F) return c - 0x3B0;       // а–я → 0x80–0x9F
        if (c >= 0x410 && c <= 0x42F) return c - 0x370;       // А–Я → 0xA0–0xBF
        if (c === 0x451) return 0xC0;                         // ё
        if (c === 0x401) return 0xC1;                         // Ё
        if (c === 0x301) return 0xC2;                         // акут
        if (c === 0x300) return 0xC3;                         // гравис
        return -1;
    }
    var DEC = (function () {
        var d = new Uint16Array(256), i;
        for (i = 0; i < 0x80; i++) d[i] = i;
        for (i = 0; i < 32; i++) { d[0x80 + i] = 0x430 + i; d[0xA0 + i] = 0x410 + i; }
        d[0xC0] = 0x451; d[0xC1] = 0x401; d[0xC2] = 0x301; d[0xC3] = 0x300;
        return d;
    })();
    function fnv(b, a, z) {
        var h = 0x811c9dc5 | 0;
        for (var i = a; i < z; i++) h = Math.imul(h ^ b[i], 0x01000193);
        return h;
    }
    /** {ключ: строка} → упакованная таблица (поля переносимы в postMessage). */
    function packMap(obj) {
        var keys = Object.keys(obj), N = keys.length, total = 0, extra = null, i, j, c, k, v;
        for (i = 0; i < N; i++) { v = obj[keys[i]]; if (typeof v === 'string') total += keys[i].length + v.length; }
        // один проход: пишем байты и считаем хеш ключа; буква вне алфавита
        // откатывает запись, и пара уходит в `extra` как есть
        var bytes = new Uint8Array(total), ko = new Int32Array(N + 1), vo = new Int32Array(N), hs = new Int32Array(N);
        var n = 0, at = 0;
        for (i = 0; i < N; i++) {
            k = keys[i]; v = obj[k];
            var start = at, h = 0x811c9dc5 | 0, ok = typeof v === 'string';
            for (j = 0; ok && j < k.length; j++) {
                c = encCh(k.charCodeAt(j));
                if (c < 0) ok = false; else { bytes[at++] = c; h = Math.imul(h ^ c, 0x01000193); }
            }
            var vs = at;
            for (j = 0; ok && j < v.length; j++) {
                c = encCh(v.charCodeAt(j));
                if (c < 0) ok = false; else bytes[at++] = c;
            }
            if (!ok) { at = start; (extra || (extra = {}))[k] = v; continue; }
            ko[n] = start; vo[n] = vs; hs[n] = h; n++;
        }
        ko[n] = at;
        var size = 2;
        while (size < n / 0.6) size *= 2;
        var tab = new Int32Array(size), mask = size - 1;
        for (i = 0; i < n; i++) {
            var s = hs[i] & mask;
            while (tab[s] !== 0) s = (s + 1) & mask;
            tab[s] = i + 1;
        }
        return { n: n, bytes: bytes, ko: ko, vo: vo, tab: tab, extra: extra };
    }
    function Packed(p) { this.n = p.n; this.bytes = p.bytes; this.ko = p.ko; this.vo = p.vo; this.tab = p.tab; this.extra = p.extra || null; }
    var KEYBUF = new Uint8Array(64), VALBUF = new Uint16Array(64);
    function packFind(P, key) {
        var n = key.length, i;
        if (KEYBUF.length < n) KEYBUF = new Uint8Array(n * 2);
        for (i = 0; i < n; i++) { var c = encCh(key.charCodeAt(i)); if (c < 0) return -1; KEYBUF[i] = c; }
        var tab = P.tab, mask = tab.length - 1, b = P.bytes, ko = P.ko, vo = P.vo;
        var s = fnv(KEYBUF, 0, n) & mask;
        for (;;) {
            var e = tab[s];
            if (e === 0) return -1;
            e--;
            var a = ko[e];
            if (vo[e] - a === n) {
                for (i = 0; i < n && b[a + i] === KEYBUF[i]; i++);
                if (i === n) return e;
            }
            s = (s + 1) & mask;
        }
    }
    function packValue(P, e) {
        var a = P.vo[e], z = P.ko[e + 1], n = z - a, b = P.bytes;
        if (VALBUF.length < n) VALBUF = new Uint16Array(n * 2);
        for (var i = 0; i < n; i++) VALBUF[i] = DEC[b[a + i]];
        return String.fromCharCode.apply(null, VALBUF.subarray(0, n));
    }
    // ── Основной словарь без JSON.parse (2026-10-16) ─────────────────────
    // ru-stress.json — плоский объект «строка → строка» без экранирования. Байты
    // UTF-8 переводятся прямо в упакованную таблицу (один проход, ни одного
    // объекта на слово): JSON.parse 25 МБ стоил столько же, сколько вся упаковка
    // после него. Что-то необычное (экранирование, не строка) — null, и словарь
    // разбирается JSON.parse, как раньше. Повтор ключа — побеждает последний, как
    // у JSON.parse.
    function scanDict(u8) {
        var N = u8.length, i = 0, c, cap = 1 << 19, n = 0, at = 0, extra = null, dec = null;
        var bytes = new Uint8Array(N), ko = new Int32Array(cap + 1), vo = new Int32Array(cap), hs = new Int32Array(cap);
        var ws = function () { while (i < N && (u8[i] === 32 || u8[i] === 10 || u8[i] === 13 || u8[i] === 9)) i++; };
        // строка JSON с кавычки на позиции i → байты в bytes с позиции at: 0 — готово,
        // -1 — нужен общий путь (JSON.parse), -2 — буква вне алфавита (строка уйдёт в extra)
        var str = function () {
            if (u8[i] !== 34) return -1;
            i++;
            var bad = false;
            for (;;) {
                if (i >= N) return -1;
                c = u8[i];
                if (c === 34) break;
                if (c === 92 || c < 32) return -1;
                if (c < 0x80) { bytes[at++] = c; i++; continue; }
                var cp = -1;
                if ((c === 0xD0 || c === 0xD1) && i + 1 < N) cp = ((c & 0x1F) << 6) | (u8[i + 1] & 0x3F);
                else if (c === 0xCC && i + 1 < N) cp = 0x300 | (u8[i + 1] & 0x3F);
                var e = cp < 0 ? -1 : encCh(cp);
                if (e < 0) { bad = true; i++; continue; }
                bytes[at++] = e; i += 2;
            }
            i++;
            return bad ? -2 : 0;
        };
        ws(); if (u8[i++] !== 123) return null;
        for (;;) {
            ws();
            if (u8[i] === 125) break;
            var start = at, kFrom = i + 1, rk = str();
            if (rk === -1) return null;
            var kEnd = i - 1, vs = at;
            ws(); if (u8[i++] !== 58) return null; ws();
            var vFrom = i + 1, rv = str();
            if (rv === -1) return null;
            if (rk === -2 || rv === -2) {
                dec = dec || new TextDecoder('utf-8');
                (extra || (extra = {}))[dec.decode(u8.subarray(kFrom, kEnd))] = dec.decode(u8.subarray(vFrom, i - 1));
                at = start;
            } else {
                if (n === cap) {
                    cap *= 2;
                    var k2 = new Int32Array(cap + 1); k2.set(ko); ko = k2;
                    var v2 = new Int32Array(cap); v2.set(vo); vo = v2;
                    var h2 = new Int32Array(cap); h2.set(hs); hs = h2;
                }
                ko[n] = start; vo[n] = vs; hs[n] = fnv(bytes, start, vs); n++;
            }
            ws();
            if (u8[i] === 44) { i++; continue; }
            if (u8[i] === 125) break;
            return null;
        }
        ko[n] = at;
        var size = 2;
        while (size < n / 0.6) size *= 2;
        var tab = new Int32Array(size), mask = size - 1, uniq = 0;
        for (var e2 = 0; e2 < n; e2++) {
            var s = hs[e2] & mask, len = vo[e2] - ko[e2];
            for (;;) {
                var t = tab[s];
                if (t === 0) { tab[s] = e2 + 1; uniq++; break; }
                var a = ko[t - 1], b2 = ko[e2], q = 0;
                if (vo[t - 1] - a === len) { while (q < len && bytes[a + q] === bytes[b2 + q]) q++; }
                if (q === len && vo[t - 1] - a === len) { tab[s] = e2 + 1; break; }   // повтор ключа: последний
                s = (s + 1) & mask;
            }
        }
        if (extra) for (var xk in extra) if (Object.prototype.hasOwnProperty.call(extra, xk)) uniq++;
        return { n: uniq, bytes: bytes.slice(0, at), ko: ko.slice(0, n + 1), vo: vo.slice(0, n), tab: tab, extra: extra };
    }

    // Словарь во время сборки: основа — упакованная таблица, правки слоёв — объект
    // поверх неё, удалённые ключи — отдельно. Те же операции, что у объекта.
    function Layered(base, over, dead) { this.base = base; this.over = over || {}; this.dead = dead || {}; }
    function mapHas(M, k) {
        if (M instanceof Layered) return mHas(M.over, k) || (!Object.prototype.hasOwnProperty.call(M.dead, k) && mHas(M.base, k));
        return Object.prototype.hasOwnProperty.call(M, k);
    }
    function mapPut(M, k, v) {
        if (M instanceof Layered) { M.over[k] = v; delete M.dead[k]; } else M[k] = v;
    }
    function mapDel(M, k) {
        if (M instanceof Layered) { delete M.over[k]; if (mHas(M.base, k)) M.dead[k] = 1; } else delete M[k];
    }
    function mapSize(M) {
        if (!(M instanceof Layered)) return Object.keys(M).length;
        var n = M.base.n, k;
        for (k in M.dead) if (Object.prototype.hasOwnProperty.call(M.dead, k)) n--;
        for (k in M.over) if (Object.prototype.hasOwnProperty.call(M.over, k) && (!mHas(M.base, k) || Object.prototype.hasOwnProperty.call(M.dead, k))) n++;
        return n;
    }

    /** Значение по ключу — из объекта, упакованной таблицы или слоёв над ней; undefined, если ключа нет. */
    function mGet(M, k) {
        if (!M) return undefined;
        if (M instanceof Packed) {
            if (M.extra && Object.prototype.hasOwnProperty.call(M.extra, k)) return M.extra[k];
            var e = packFind(M, k);
            return e < 0 ? undefined : packValue(M, e);
        }
        if (M instanceof Layered) {
            if (mHas(M.over, k)) return mGet(M.over, k);
            return Object.prototype.hasOwnProperty.call(M.dead, k) ? undefined : mGet(M.base, k);
        }
        return Object.prototype.hasOwnProperty.call(M, k) ? M[k] : undefined;
    }
    // Омографы в упакованной таблице — формы через \u0001; массив собирается один раз
    // на слово и дальше отдаётся тот же (как раньше — тот же массив из объекта).
    var HOMO_SEP = '\u0001', homoSplit = null;
    function homoGet(k) {
        if (!HOMO) return null;
        if (!(HOMO instanceof Packed)) return Object.prototype.hasOwnProperty.call(HOMO, k) ? HOMO[k] : null;
        if (homoSplit && Object.prototype.hasOwnProperty.call(homoSplit, k)) return homoSplit[k];
        var v = mGet(HOMO, k);
        if (v === undefined) return null;
        (homoSplit || (homoSplit = {}))[k] = v = String(v).split(HOMO_SEP);
        return v;
    }
    function mHas(M, k) {
        if (!M) return false;
        if (M instanceof Packed) return (!!M.extra && Object.prototype.hasOwnProperty.call(M.extra, k)) || packFind(M, k) >= 0;
        if (M instanceof Layered) return mapHas(M, k);
        return Object.prototype.hasOwnProperty.call(M, k);
    }

    function lookup(word) {
        var k = foldKey(word);
        if (NORM && Object.prototype.hasOwnProperty.call(NORM, k)) return { type: 'single', forms: [NORM[k].form], source: 'norm', src: NORM[k].src };
        var d = mGet(SINGLE, k);
        if (d === undefined) d = null;
        var h = homoGet(k);
        var y = h ? null : yoForm(k);
        if (y && (!d || (!sameYo(d, y) && mainVowel(d) === mainVowel(y)))) {
            return { type: 'single', forms: [y], source: 'yo' };
        }
        if (d) return { type: 'single', forms: [/[ёЁ]/.test(d) ? mainOnly(d) : oneMark(d)] };
        if (h) return { type: 'homograph', forms: h };
        return { type: 'miss', forms: [] };
    }

    // Ordinals of every acute-marked vowel of a form.
    function markedVowels(form) {
        var out = [], n = -1, low = String(form).toLowerCase();
        for (var i = 0; i < low.length; i++) {
            if (VOWELS.indexOf(low[i]) < 0) continue;
            n++;
            if (low[i + 1] === ACUTE) out.push(n);
        }
        return out;
    }
    // A dictionary form marked twice keeps its FIRST mark. The CC-BY-SA source gives a
    // word with two acceptable stresses one form with both («фро́нта́», «несча́стли́в»,
    // «у́зки́» — 60 entries), and both lanes read it as a word stressed twice. Mirror
    // of _one_mark() in services/silero-tts/accent.py.
    function oneMark(form) {
        var i = form.indexOf(ACUTE);
        if (i < 0 || form.indexOf(ACUTE, i + 1) < 0) return form;
        return form.slice(0, i + 1) + form.slice(i + 1).split(ACUTE).join('');
    }
    // The readings of a homograph, each with one mark: a doubly marked reading whose
    // stress another reading already gives is dropped («при́го́ршня» next to
    // «приго́ршня»), else it keeps its first mark. Mirror of _one_mark_forms().
    function oneMarkForms(forms) {
        var single = {}, out = [], i;
        for (i = 0; i < forms.length; i++) {
            var at = markedVowels(forms[i]);
            if (at.length === 1) single[at[0]] = 1;
        }
        for (i = 0; i < forms.length; i++) {
            var f = forms[i], m = markedVowels(f), covered = false;
            if (m.length > 1) {
                for (var j = 0; j < m.length; j++) if (single[m[j]] === 1) covered = true;
                if (covered) continue;
                f = oneMark(f);
            }
            if (out.indexOf(f) < 0) out.push(f);
        }
        return out;
    }

    /** ё-форма слова по словарю eyo (нижний регистр) или null. */
    function yoForm(k) {
        if (!YO || (YO_SKIP && Object.prototype.hasOwnProperty.call(YO_SKIP, k))) return null;
        var y = mGet(YO, k);
        if (typeof y !== 'string') return null;
        y = y.toLowerCase();
        return foldKey(y) === k && countVowels(y) > 1 ? y : null;
    }

    /** Все «ё» слова `yo` стоят в форме `form` на тех же местах: это то же слово. */
    function sameYo(form, yo) {
        var a = String(form).replace(/[+́̀]/g, '').toLowerCase();
        var b = String(yo).replace(/[+́̀]/g, '').toLowerCase();
        if (a.length !== b.length) return false;
        for (var i = 0; i < b.length; i++) if (b[i] === 'ё' && a[i] !== 'ё') return false;
        return true;
    }

    /**
     * Порядковый номер гласной ГЛАВНОГО ударения: самый правый знак (акут или
     * «+») или «ё». В сложном слове первая часть несёт побочное ударение:
     * «трёхэта́жный», «вперёдсмотря́щий» — главное дальше, «госсекретарём»,
     * «высокоодарённый» — главное на последней «ё». -1, если знака нет.
     */
    function mainVowel(form) {
        var low = String(form).toLowerCase(), n = -1, at = -1, grave = -1;
        for (var i = 0; i < low.length; i++) {
            if (VOWELS.indexOf(low[i]) < 0) continue;
            n++;
            if (low[i] === 'ё' || low[i + 1] === ACUTE || low[i - 1] === '+') at = n;
            else if (low[i + 1] === '̀') grave = n;
        }
        return at >= 0 ? at : grave;
    }

    /**
     * Форма с «ё» и знаком: знак остаётся только у главного ударения.
     * «го́ссекретарём» → «госсекретарём» (главное — «ё»), «трёхэта́жный» — как есть.
     */
    function mainOnly(form) {
        var main = mainVowel(form), n = -1, out = '';
        for (var i = 0; i < form.length; i++) {
            var ch = form[i];
            if (ch === ACUTE || ch === '̀') {
                if (n === main && !/[ёЁ]/.test(form[i - 1])) out += ACUTE;
                continue;
            }
            if (VOWELS.indexOf(ch.toLowerCase()) >= 0) n++;
            out += ch;
        }
        return out;
    }

    /**
     * Слово, НАПИСАННОЕ с «ё»: словарная форма, если словарь знает это самое
     * написание и главное ударение у него не на «ё» («трёхэта́жный»), иначе null —
     * ударение несёт сама «ё» (у нескольких «ё» — последняя).
     */
    function yoWordForm(word) {
        if (!/[ёЁ]/.test(word)) return null;
        var k = foldKey(word);
        var d = mGet(SINGLE, k);
        if (!d || !sameYo(d, word)) return null;
        var m = mainOnly(d);
        return m.indexOf(ACUTE) >= 0 ? m : null;
    }

    // безударные частицы, пишущиеся через дефис ПОСЛЕ слова
    var ENCLITICS = { 'то': 1, 'либо': 1, 'нибудь': 1, 'ка': 1, 'таки': 1, 'де': 1, 'с': 1 };
    function isEnclitic(w) { return ENCLITICS[String(w).toLowerCase()] === 1; }

    // Слово с дефисом так, как его прочтут голоса: по частям, частица после
    // дефиса без ударения. Возвращает размеченное слово («како́й-то»), само слово,
    // если ставить нечего («кто-то», «из-за»), или null, если какую-то часть без
    // человека не решить — её нет в словаре или это омограф. /russian-stress
    // держит такое слово одним токеном и до 29.09.2026 искал его в словаре
    // целиком: промах, красная подсветка, просьба о ручной пометке — и
    // «како́й-то», «кто́-то» уходили в урожай «новыми словами», которые ни один
    // голос не читает (accentuate() режет слово на дефисе).
    function lookupCompound(raw) {
        var parts = String(raw).split('-'), out = [];
        if (parts.length < 2 || /[+́̀]/.test(raw)) return null;
        for (var i = 0; i < parts.length; i++) {
            var p = parts[i];
            if (!p) return null;
            if (i > 0 && isEnclitic(p)) { out.push(p); continue; }
            if (countVowels(p) < 2 || /[ёЁ]/.test(p)) { out.push(p); continue; }
            var r = lookup(p);
            var form = r.type === 'single' ? r.forms[0] : (r.type === 'miss' ? guess(p) : null);
            if (!form) return null;
            out.push(applyCase(p, form));
        }
        return out.join('-');
    }

    function accentuateWord(tok, opts) {
        // Ручная пометка человека сильнее любого словаря. Знак «+» переносится на
        // БЛИЖАЙШУЮ ГЛАСНУЮ ПОСЛЕ него, а не только на соседнюю: человек ставит
        // его по слуху и попадает то перед гласной, то перед согласной («По+ля»).
        // Оставшийся «+» удаляется в любом случае — замер 18.09 на qwen-tts:
        // модель плюс НЕ понимает и растягивает слово (0.66 и 0.91 с против 0.38
        // без знака), то есть буквальный плюс в тексте хуже, чем отсутствие
        // пометки. До 18.09 «+» перед согласной уезжал в модель как есть.
        if (opts.skipMarked !== false && /[+́̀]/.test(tok)) {
            var _V = '[' + VOWELS + VOWELS.toUpperCase() + ']';
            var _NV = '[^' + VOWELS + VOWELS.toUpperCase() + '+]';
            return tok
                .replace(new RegExp('[+](' + _NV + '*)(' + _V + ')', 'g'), '$1$2' + ACUTE)
                // «ё» ударна сама по себе: акут поверх неё («шё́пот») — двойной
                // диакритик, который модели только мешает (в финальном тексте
                // #6850 так ушло слово «шё́пот» после ручной пометки).
                .replace(new RegExp('([ёЁ])' + ACUTE, 'g'), '$1')
                .replace(/\+/g, '');
        }
        // Имя с заглавной буквы — раньше словаря и раньше ё-правила: слой имён
        // сам несёт ё («Артем» → «Артём»), а решает он по регистру, которого
        // словарь не видит. Со строчной буквы слой молчит, и «паша́» остаётся
        // титулом.
        var nf = nameForm(tok);
        if (nf) return applyCase(tok, nf);
        // ё is inherently stressed → respect an existing ё, never override it (e.g. keep the
        // input «витёк», don't fold to a dictionary «ви́тек»). ё-less words still get ё restored below.
        // The one exception is the dictionary's own spelling of THIS word as a compound
        // whose main stress follows the ё («трёхэта́жный») — see yoWordForm().
        if (tok.indexOf('ё') !== -1 || tok.indexOf('Ё') !== -1) {
            var yw = yoWordForm(tok);
            return yw ? applyCase(tok, yw) : tok;
        }
        // single-vowel words don't need a stress mark (stress is unambiguous)
        if (countVowels(tok) < 2) {
            // still ё-restore via dict if available
            // ⚠️ Форма словаря несёт и акут («где́», «жи́л», «то́»), а комментарий
            // выше обещает обратное. У слова с одной гласной знак не сообщает
            // ничего, зато в «кто́-то́» ставит ударение на частицу, которой оно
            // не положено никогда. Берём из словаря только восстановление «ё».
            var r0 = lookup(tok);
            if (r0.type === 'single') return applyCase(tok, r0.forms[0].split(ACUTE).join(''));
            return tok;
        }
        var r = lookup(tok);
        if (r.type === 'single') return applyCase(tok, r.forms[0]);
        if (r.type === 'homograph') {
            if (opts.homograph === 'first') return applyCase(tok, r.forms[0]);
            return tok; // leave unmarked — TTS guesses; user can mark manually
        }
        var g = guess(tok);
        if (g) return applyCase(tok, g); // догадка акцентора — только там, где словари молчат
        return tok; // OOV
    }

    /**
     * Форма из слоя имён или null — только для слова с заглавной буквы: override
     * перебивает словарь, обычная запись лишь дополняет его («Поля зеленели»
     * остаётся поля́). Одна функция для accentuate() и для тех, кто разбирает
     * слова сам: /russian-stress до 30.09.2026 слой имён не видел вовсе — «Маша»
     * была красной, «Паша» выходил «Паша́» (титул).
     */
    function nameForm(word) {
        if (!CAP_RE.test(word)) return null;
        var nk = foldKey(word);
        if (NAMES_OVR && Object.prototype.hasOwnProperty.call(NAMES_OVR, nk)) return NAMES_OVR[nk];
        if (NAMES && Object.prototype.hasOwnProperty.call(NAMES, nk)
            && !mHas(SINGLE, nk)
            && !mHas(HOMO, nk)
            && !Object.prototype.hasOwnProperty.call(NORM || {}, nk)) {
            return NAMES[nk];
        }
        return null;
    }

    /** Ударная форма из машинного слоя или null. Словарём она не считается. */
    function guess(word) {
        var k = foldKey(word);
        if (NORM && Object.prototype.hasOwnProperty.call(NORM, k)) return null;   // у нормы ответ есть
        if (yoForm(k)) return null;                                                // у «ё» тоже
        var g = mGet(GUESS, k);
        return g === undefined ? null : g;
    }

    /** Запись нормы {form, src} или null — для страниц, которые показывают источник. */
    function norm(word) {
        var k = foldKey(word);
        return NORM && Object.prototype.hasOwnProperty.call(NORM, k) ? { form: NORM[k].form, src: NORM[k].src } : null;
    }

    // Запись нормы принимается, только если она — ТО ЖЕ слово с ОДНИМ ударением
    // (знак или «ё») и источник объявлен в самом файле. Иначе запись молча
    // пропускается: слой, который нельзя проверить, не должен перебивать словарь.
    function takeNorm(json) {
        var out = {}, n = 0;
        var words = (json && json.words) || {}, sources = (json && json.sources) || {};
        for (var k in words) {
            if (!Object.prototype.hasOwnProperty.call(words, k)) continue;
            var e = words[k];
            if (!e || typeof e.form !== 'string' || !Object.prototype.hasOwnProperty.call(sources, e.src)) continue;
            var f = cleanForm(e.form);
            var marks = f.split(ACUTE).length - 1 + (/[ёЁ]/.test(f) ? 1 : 0);
            if (!f || foldKey(f) !== foldKey(k) || marks !== 1 || countVowels(f) < 2) continue;
            out[foldKey(k)] = { form: f.toLowerCase(), src: e.src }; n++;
        }
        return { map: out, count: n };
    }

    // Слой решений по «ё» — под теми же проверками, что у сервера
    // (accent.py `_load_yo_layer`): форма обязана быть ТЕМ ЖЕ словом и нести
    // ударение (знак или «ё»). `forms` → {ключ: [формы]}, `skip` → {ключ: 1}
    // для всех слов слоя: eyo их больше не спрашивают.
    function takeYoLayer(json) {
        var forms = {}, skip = {}, n = 0;
        var src = (json && json.forms) || {};
        for (var k in src) {
            if (!Object.prototype.hasOwnProperty.call(src, k)) continue;
            var list = Array.isArray(src[k]) ? src[k] : [src[k]], key = foldKey(k), ok = [];
            for (var i = 0; i < list.length; i++) {
                var f = typeof list[i] === 'string' ? cleanForm(list[i]).toLowerCase() : '';
                if (f && foldKey(f) === key && mainVowel(f) >= 0 && ok.indexOf(f) < 0) ok.push(f);
            }
            if (ok.length) { forms[key] = ok; skip[key] = 1; n++; }
        }
        var sk = (json && json.skip_yo) || [];
        for (var j = 0; j < sk.length; j++) if (typeof sk[j] === 'string') skip[foldKey(sk[j])] = 1;
        return { forms: forms, skip: skip, count: n };
    }

    // Исправления базового словаря (ru-stress-errata.json, 2026-10-16): чтения,
    // которые CC-BY-SA-словарь ставит неверно («происходи́т», «нико́го»), ложные
    // чтения омографов («тво́ему») и слова, которых нет ни в одном слое. Каждая
    // запись засвидетельствована обоими Викисловарями и/или акцентором silero-stress
    // (services/silero-tts/build_stress_errata.py) — тот же файл читают голос
    // (accent.py `_load_errata`) и словарь стиха (BuildRuVerseData).
    function takeErrata(json) {
        var fix = {}, drop = {}, add = {}, n = 0;
        var good = function (f, key) {
            var c = typeof f === 'string' ? cleanForm(f).toLowerCase() : '';
            return c && foldKey(c) === key && mainVowel(c) >= 0 ? c : '';
        };
        var src = (json && json.fix) || {};
        for (var k in src) {
            if (!Object.prototype.hasOwnProperty.call(src, k) || !src[k]) continue;
            var key = foldKey(k), list = [src[k].form].concat(Array.isArray(src[k].also) ? src[k].also : []), ok = [];
            for (var i = 0; i < list.length; i++) { var f = good(list[i], key); if (f && ok.indexOf(f) < 0) ok.push(f); }
            if (ok.length) { fix[key] = ok; n++; }
        }
        var dr = (json && json.drop) || {};
        for (var dk in dr) {
            if (!Object.prototype.hasOwnProperty.call(dr, dk) || !dr[dk] || !Array.isArray(dr[dk].keep)) continue;
            var dkey = foldKey(dk), keep = [];
            for (var j = 0; j < dr[dk].keep.length; j++) { var kf = good(dr[dk].keep[j], dkey); if (kf && keep.indexOf(kf) < 0) keep.push(kf); }
            if (keep.length) { drop[dkey] = keep; n++; }
        }
        var ad = (json && json.add) || {};
        for (var ak in ad) {
            if (!Object.prototype.hasOwnProperty.call(ad, ak)) continue;
            var akey = foldKey(ak), af = good(ad[ak], akey);
            if (af) { add[akey] = af; n++; }
        }
        return { fix: fix, drop: drop, add: add, count: n };
    }

    // `fix` и `drop` заменяют словарь (и догадку), `add` — только там, где молчат
    // все слои. Исправленное слово ёфикатор не трогает: «коронер» уходил в
    // «коронёр» раньше, чем словарь успевал ответить.
    function applyErrata(er, single, homo, guesses, skip) {
        var put = function (k, forms) {
            if (forms.length === 1) { mapPut(single, k, forms[0]); if (homo) delete homo[k]; }
            else if (homo) { homo[k] = forms.slice(); mapDel(single, k); }
            if (guesses) delete guesses[k];
            if (skip) skip[k] = 1;
        };
        for (var k in er.fix) if (Object.prototype.hasOwnProperty.call(er.fix, k)) put(k, er.fix[k]);
        for (var d in er.drop) if (Object.prototype.hasOwnProperty.call(er.drop, d)) put(d, er.drop[d]);
        for (var a in er.add) {
            if (!Object.prototype.hasOwnProperty.call(er.add, a)) continue;
            if (mapHas(single, a) || (homo && Object.prototype.hasOwnProperty.call(homo, a))) continue;
            if (guesses && Object.prototype.hasOwnProperty.call(guesses, a)) delete guesses[a];
            mapPut(single, a, er.add[a]);
        }
    }

    // Слова, где «ё» решает предложение (см. YO_CTX): `words` и `context_words`
    // ru-stress-yo-model.json, ключ — слово без «ё» и без знаков (как у сервера
    // `_load_yo_allow`). Остальное в файле — замер, лейны его не читают.
    function takeYoModel(json) {
        var out = {}, n = 0, keys = [];
        var w = (json && json.words) || {};
        for (var k in w) if (Object.prototype.hasOwnProperty.call(w, k)) keys.push(k);
        keys = keys.concat(Array.isArray(json && json.context_words) ? json.context_words : []);
        for (var i = 0; i < keys.length; i++) {
            if (typeof keys[i] !== 'string' || !/^[а-яё]+$/i.test(keys[i])) continue;
            var key = foldKey(keys[i]);
            if (!out[key]) { out[key] = 1; n++; }
        }
        return { map: n ? out : null, count: n };
    }

    // Решённые чтения ложатся в словарь: одно — в SINGLE, несколько — омограф
    // (первым — более частое), как у сервера.
    function applyYoLayer(layer, single, homo, guesses) {
        for (var k in layer.forms) {
            if (!Object.prototype.hasOwnProperty.call(layer.forms, k)) continue;
            var f = layer.forms[k];
            if (f.length === 1) { mapPut(single, k, f[0]); if (homo) delete homo[k]; }
            else { homo[k] = f.slice(); mapDel(single, k); }
            if (guesses) delete guesses[k];
        }
    }

    // Словарь eyo — тот же скрипт, что грузят переключатель «Восстановить ё» на
    // /russian-stress и /yofikator (80 КБ в gzip, кэшируется). Нет окна (тесты)
    // или скрипт не пришёл — null, и лейн работает как до 01.10.2026.
    var yoLoading = null;
    // Воркер разбора (см. load): окна нет, скрипт eyo берёт importScripts.
    var IN_WORKER = typeof self !== 'undefined' && self.__RA_WORKER === 1 && typeof importScripts === 'function';
    function eyoDict() {
        var g = IN_WORKER ? self : (typeof window !== 'undefined' ? window : null);
        return (g && g.safeEyo && g.safeEyo.dictionary && g.safeEyo.dictionary.dict) || null;
    }
    function loadYoDict(url) {
        if (IN_WORKER) {
            // после микрозадачи: запрос словаря ударений уже ушёл в сеть, а
            // синхронный importScripts не задерживает его старт
            return Promise.resolve().then(function () {
                if (!eyoDict()) { try { importScripts(url); } catch (_) { return null; } }
                return eyoDict();
            });
        }
        if (typeof window === 'undefined' || typeof document === 'undefined') return Promise.resolve(null);
        if (eyoDict()) return Promise.resolve(eyoDict());
        if (yoLoading) return yoLoading;
        yoLoading = new Promise(function (resolve) {
            var s = document.createElement('script');
            s.src = url; s.async = true;
            // строгий CSP пропускает скрипт с тем же nonce, что у страницы
            try { var ns = document.querySelector('script[nonce]'); if (ns && ns.nonce) s.nonce = ns.nonce; } catch (_) {}
            s.onload = function () { resolve(eyoDict()); };
            s.onerror = function () { yoLoading = null; resolve(null); };
            (document.head || document.documentElement).appendChild(s);
        });
        return yoLoading;
    }

    // Норма убирает слово из списков омографов и догадок: у «приданое» одно
    // чтение, и панель выбора, запрос к серверу за контекстом и кнопка ударения
    // больше не предлагают второе.
    function applyNorm(normMap, homo, guesses) {
        for (var k in normMap) {
            if (!Object.prototype.hasOwnProperty.call(normMap, k)) continue;
            if (homo && Object.prototype.hasOwnProperty.call(homo, k)) delete homo[k];
            if (guesses && Object.prototype.hasOwnProperty.call(guesses, k)) delete guesses[k];
        }
    }

    // ── Context homographs — readings that flip by NEIGHBOR, not by morphology ──
    // Mirror of CTX_RULES in services/silero-tts/accent.py (the server lane had
    // these alone for three weeks — the exact «список внутри одного лейна — это
    // половина исправления» class from 2026-08-07): «утра» is «в семь утра́ /
    // с утра́ / до утра́» in time phrases but «до́брого у́тра» in the greeting.
    // A flat overlay default picks one side at the cost of the other (and did:
    // the 08-27 harvest's «утра́» default made the greeting wrong on BOTH lanes),
    // so the pick happens here, where the neighbor is visible, by planting a '+'
    // — the manual-mark form accentuateWord() already honors. The harvest now
    // refuses to (re)learn these words (StressHarvest::CONTEXT_OWNED).
    // No lookbehind: Safari < 16.4 throws at PARSE time of the whole file.
    // The trailing guard also skips input the user already marked («утр+а», «утра́»).
    var UTRA_NUM = ('один одна одно два две три четыре пять шесть семь восемь девять ' +
        'десять одиннадцать двенадцать первого второго третьего четвертого ' +
        'четвёртого пятого шестого седьмого восьмого девятого десятого ' +
        'одиннадцатого двенадцатого часов часа часу ' +
        // the genitive of the hour after «до / с / около» — «до четырёх утра́»
        'двух трёх трех четырёх четырех пяти шести семи восьми девяти десяти ' +
        'одиннадцати двенадцати').split(' ');
    // «облит по́том», «холодным по́том» — the one place «потом» is the noun «пот»
    var POT_BEFORE = ('холодным горячим липким крупным солёным соленым кровавым едким обильным ' +
        'трудовым собственным ледяным вонючим ' +
        'облит облита облито облиты облитый облитая облитое облитые обливаясь обливался ' +
        'обливалась обливались облился облилась облились покрылся покрылась покрылись ' +
        'покрыт покрыта покрыто покрыты покрытый покрытая покрытое покрытые ' +
        'пропитан пропитана пропитано пропитаны пропитанный пропитанная пропитанные ' +
        'пропах пропахла пропахло пропахли пахнет пахло пахла пахли пахнуть пахнущий ' +
        'пахнущая пахнущие воняет воняло вонял воняли вонять разит разило ' +
        'залит залита залито залиты залитый залитая залитые').split(' ');
    // «отзы́в лицензии / посла» — the recall: the next word names what is recalled
    var OTZYV_WHAT = ('лицензи посла послу послов послам соглас доверенност заявлени заявк иска исков ' +
        'жалоб депутат кандидатур подписи разрешени аккредитаци оферт акцепт приказа ' +
        'сертификат продукци автомобилей партии полномочи ходатайств войск дипломат ' +
        'законопроект').split(' ');
    // «вы захо́дите» — after «вы» the indicative, alone «заходи́те» (the imperative)
    var HODITE_PREFIXES = 'за под вы при у пере про в от об до на вос вс ис'.split(' ');
    var NOT_AFTER = '(?![А-Яа-яЁё+́̀])';
    // The regex's LAST group is the word; `at` — its stressed vowel's ordinal, or a
    // function of the word that gives it. Case-blind («Пахнет по́том»); the '+' lands
    // on the word as typed.
    function ctxRule(src, at) { return { re: new RegExp(src, 'gi'), at: at }; }
    var CTX_RULES = [
        // …N утра́ (numeral/hour word before) — «в семь утра́», «до четырёх утра́»
        ctxRule('(^|[^А-Яа-яЁё])((?:' + UTRA_NUM.join('|') + ')\\s+)(утра)' + NOT_AFTER, 1),
        // adverbial «с утра́» / «до утра́», «с самого утра́» (the proclitic itself stays unmarked)
        ctxRule('(^|[^А-Яа-яЁё])((?:[сС]|[дД]о)\\s+(?:самого\\s+)?)(утра)' + NOT_AFTER, 1),
        // «до упа́ду» — the idiom; the verb is «упаду́»
        ctxRule('(^|[^А-Яа-яЁё])([дД]о\\s+)(упаду)' + NOT_AFTER, 1),
        // «взять в поло́н»
        ctxRule('(^|[^А-Яа-яЁё])([вВ]о?\\s+)(полон)' + NOT_AFTER, 1),
        // «холодным по́том», «облит по́том», «по́том и кровью»
        ctxRule('(^|[^А-Яа-яЁё])((?:' + POT_BEFORE.join('|') + ')\\s+)(потом)' + NOT_AFTER, 0),
        ctxRule('(^|[^А-Яа-яЁё])((?:[кК]ровью|[сС]лезами|[тТ]рудом)\\s+и\\s+)(потом)' + NOT_AFTER, 0),
        ctxRule('(^|[^А-Яа-яЁё])([пП]отом)(?=\\s+и\\s+кровью)', 0),
        // «отзы́в лицензии», «Отзы́в посла»
        ctxRule('(^|[^А-Яа-яЁё])([оО]тзыв(?:а|у|ом|е)?)(?=\\s+(?:' + OTZYV_WHAT.join('|') + '))', 1),
        // «вы захо́дите», «вы выхо́дите» — the stressed «о» of «-ход-»
        ctxRule('(^|[^А-Яа-яЁё])([вВ]ы\\s+)((?:' + HODITE_PREFIXES.join('|') + ')?ходите)' + NOT_AFTER,
            function (w) { return countVowels(w.slice(0, w.length - 'ходите'.length)); }),
    ];
    function markNthVowel(w, n) {
        for (var i = 0, seen = 0; i < w.length; i++) {
            if (VOWELS.indexOf(w[i].toLowerCase()) < 0) continue;
            if (seen === n) return w.slice(0, i) + '+' + w.slice(i);
            seen++;
        }
        return w;
    }
    function applyCtxRules(t) {
        for (var ci = 0; ci < CTX_RULES.length; ci++) {
            var rule = CTX_RULES[ci];
            t = t.replace(rule.re, function () {
                // the groups are every argument between the match and its offset
                var groups = [];
                for (var a = 1; a < arguments.length && typeof arguments[a] !== 'number'; a++) groups.push(arguments[a] || '');
                var word = groups[groups.length - 1];
                var n = typeof rule.at === 'function' ? rule.at(word) : rule.at;
                return groups.slice(0, -1).join('') + markNthVowel(word, n);
            });
        }
        return t;
    }

    function accentuate(text, opts) {
        opts = opts || {};
        if (!ready) return text;
        var t = applyCtxRules(String(text));
        return t.replace(WORD_RE, function (m, off, whole) {
            // Частица после дефиса безударна: «когда́-нибудь», «кто́-либо»,
            // «всё-таки». Словарь знает её как отдельное слово («нибу́дь») и ставил
            // второе ударение в одно слово. Индекс совпадения у replace идёт
            // ПОСЛЕДНИМ числовым аргументом, поэтому берём его из arguments.
            var at = -1;
            for (var ai = arguments.length - 1; ai >= 1; ai--) if (typeof arguments[ai] === 'number') { at = arguments[ai]; break; }
            var src = (typeof arguments[arguments.length - 1] === 'string') ? arguments[arguments.length - 1] : t;
            if (at > 0 && src.charAt(at - 1) === '-' && ENCLITICS[m.toLowerCase()] === 1
                && !/[+́]/.test(m)) return m;
            return accentuateWord(m, opts);
        });
    }

    // A "homograph" whose readings collapse to ONE is not a homograph — it is a
    // broken entry, and the review panel asked the user to choose between two
    // identical buttons. The dict has exactly one such entry, and it is the most
    // common word in Russian: «что» → ["'что́","что́"]. It therefore surfaced in
    // nearly every text (1794 of 9443 flywheel events, 679 distinct users) and
    // its first button pasted a literal apostrophe into the textarea. A single
    // surviving reading is promoted into the single dict when it is worth having
    // (≥2 vowels — with one vowel the stress is unambiguous and a mark is noise).
    // Returns how many entries were retired. Mirrored in services/silero-tts/accent.py.
    function sanitizeHomographs(single, homo) {
        var dropped = 0;
        for (var k in homo) {
            if (!Object.prototype.hasOwnProperty.call(homo, k)) continue;
            var forms = homo[k], seen = {}, clean = [];
            for (var i = 0; forms && i < forms.length; i++) {
                var cf = cleanForm(forms[i]);
                if (cf && !Object.prototype.hasOwnProperty.call(seen, cf)) { seen[cf] = 1; clean.push(cf); }
            }
            clean = oneMarkForms(clean);
            if (clean.length > 1) { homo[k] = clean; continue; }
            if (clean.length === 1 && countVowels(clean[0]) > 1
                && !mapHas(single, k)) mapPut(single, k, clean[0]);
            delete homo[k];
            dropped++;
        }
        return dropped;
    }

    // Which reading of a homograph goes FIRST — the flat one, said wherever no rule
    // fires and the server's model is not asked (tembrica.com has no model at all).
    // `json.order` (ru-stress-order.json, built by services/silero-tts/
    // build_homograph_order.py): the silero-stress accentor's reading of the word
    // alone where it is not the dictionary's first — the dictionary lists readings by
    // position («ле́карство», «ко́стей», «но́ски» came first). Matched by the stressed
    // vowel, not the string. Returns how many lists moved. Mirror of accent.py _load_order.
    function applyOrder(json, homo) {
        var src = (json && json.order) || {}, n = 0;
        for (var k in src) {
            if (!Object.prototype.hasOwnProperty.call(src, k) || typeof src[k] !== 'string') continue;
            if (moveFirst(homo, k, cleanForm(src[k]).toLowerCase())) n++;
        }
        return n;
    }
    // The reading of `homo[k]` stressed where `form` is goes first; true if it is there.
    function moveFirst(homo, k, form) {
        var forms = homo && Object.prototype.hasOwnProperty.call(homo, k) ? homo[k] : null;
        if (!forms || !forms.length || foldKey(form) !== k) return false;
        var at = mainVowel(form);
        for (var i = 0; i < forms.length; i++) {
            if (mainVowel(forms[i]) !== at) continue;
            if (i > 0) forms.unshift(forms.splice(i, 1)[0]);
            return true;
        }
        return false;
    }

    // Только омографы — 72 КБ против 3.6 МБ полного словаря. Этого хватает,
    // чтобы `lookup()` знал ОБА чтения спорного слова, а значит кнопка
    // «Поставить ударение» перебирала гласные в словарном порядке
    // («но́мера» → «номера́»), а не слева направо. `isReady()` при этом остаётся
    // false: `accentuate()` и `contextPicks()` без основного словаря молчат,
    // как и раньше. Вызывается, когда кнопка ударения появилась на экране.
    var homoOnlyLoading = null;
    async function loadHomographs(opts) {
        if (ready || HOMO) return true;
        if (homoOnlyLoading) return homoOnlyLoading;
        opts = opts || {};
        var base = opts.base || '/vendor/ru-accent';
        var fetchJson = opts.fetchJson || async function (u) {
            var r = await fetch(u, { cache: 'force-cache' });
            if (!r.ok) throw new Error('fetch ' + u + ' ' + r.status);
            return await r.json();
        };
        // Норма едет вместе с омографами: кнопка ударения на «приданое» обязана
        // сразу ставить знак по норме, а не предлагать ложное «при́даное».
        var normP = (typeof fetch === 'function'
            ? fetch(opts.normUrl || '/data/lang/ru-stress-norm.json', { cache: 'no-cache' })
                .then(function (r) { return r.ok ? r.json() : null; })
            : Promise.resolve(null)).catch(function () { return null; });
        var small = function (url) {
            return (typeof fetch === 'function'
                ? fetch(url, { cache: 'no-cache' }).then(function (r) { return r.ok ? r.json() : null; })
                : Promise.resolve(null)).catch(function () { return null; });
        };
        // ложные чтения омографов («тво́ему») не должны всплыть и в лёгком пути
        var errataP = small(opts.errataUrl || '/data/lang/ru-stress-errata.json');
        // первое чтение кнопки — то же, что у полного пути: порядок акцентора, поверх
        // него выбор людей (до 22.10.2026 лёгкий путь брал порядок словаря: «ле́карство»)
        var orderP = small(opts.orderUrl || '/data/lang/ru-stress-order.json');
        var communityP = small(opts.communityUrl || '/data/lang/ru-stress-community.json');
        homoOnlyLoading = Promise.all([fetchJson(base + '/ru-stress-homographs.json'), normP, errataP, orderP, communityP]).then(function (both) {
            if (ready) return true;                       // полный словарь успел приехать
            var h = both[0], clean = {};
            for (var k in h) {
                if (!Object.prototype.hasOwnProperty.call(h, k)) continue;
                clean[k] = h[k];
            }
            sanitizeHomographs({}, clean);                // та же чистка, что у полного пути
            applyOrder(both[3], clean);
            var cd = (both[4] && both[4].defaults) || {};
            for (var dk in cd) {
                if (Object.prototype.hasOwnProperty.call(cd, dk) && typeof cd[dk] === 'string') moveFirst(clean, foldKey(dk), cleanForm(cd[dk]).toLowerCase());
            }
            if (both[2]) {
                // одиночные исправления лёгкому пути не нужны: он знает только омографы
                var et = takeErrata(both[2]);
                for (var ek in clean) {
                    if (!Object.prototype.hasOwnProperty.call(clean, ek)) continue;
                    var forms = et.fix[ek] || et.drop[ek];
                    if (!forms) continue;
                    if (forms.length > 1) clean[ek] = forms.slice(); else delete clean[ek];
                }
            }
            if (both[1]) { NORM = takeNorm(both[1]).map; applyNorm(NORM, clean, null); }
            HOMO = clean;
            return true;
        }).catch(function () { homoOnlyLoading = null; return false; });
        return homoOnlyLoading;
    }

    // Источники полной загрузки: одно место для главного потока и для воркера
    // (воркеру load() отдаёт их абсолютными — у blob-воркера нет базы для «/…»).
    var SRC = {
        base: '/vendor/ru-accent', yoUrl: '/vendor/eyo/bundle.safe.js',
        curatedUrl: '/data/lang/ru-stress-curated.json', communityUrl: '/data/lang/ru-stress-community.json',
        namesUrl: '/data/lang/ru-stress-names.json', accentorUrl: '/data/lang/ru-stress-accentor.json',
        yoLayerUrl: '/data/lang/ru-stress-yo.json', yoModelUrl: '/data/lang/ru-stress-yo-model.json',
        errataUrl: '/data/lang/ru-stress-errata.json', additionsUrl: '/data/lang/ru-stress-additions.json',
        normUrl: '/data/lang/ru-stress-norm.json', orderUrl: '/data/lang/ru-stress-order.json'
    };
    function srcUrl(opts, key) { return (opts && opts[key]) || SRC[key]; }

    async function loadMain(opts) {
        opts = opts || {};
        var base = srcUrl(opts, 'base');
        // Все источники запрашиваются сразу, а разбираются по порядку: раньше
        // каждый следующий запрос ждал разбора предыдущего — двенадцать круговых
        // задержек подряд (на мобильной сети RTT 150 мс — почти 2 с).
        var early = {};
        var get = function (u, mode) {
            var e = early[u];
            if (e && e.mode === mode) { delete early[u]; return e.p; }
            return fetch(u, { cache: mode });
        };
        // `bytes` — буферы, которые страница скачала сама (воркер, opts.fetchBytes)
        var bytes = opts.bytes || null;
        var fetchJson = opts.fetchJson || async function (u) {
            if (bytes && Object.prototype.hasOwnProperty.call(bytes, u)) {
                if (!bytes[u]) throw new Error('fetch ' + u);
                return JSON.parse(new TextDecoder('utf-8').decode(bytes[u]));
            }
            var r = await get(u, 'force-cache');
            if (!r.ok) throw new Error('fetch ' + u + ' ' + r.status);
            return await r.json();
        };
        if (typeof fetch === 'function') {
            var go = function (u, mode) {
                if (bytes && Object.prototype.hasOwnProperty.call(bytes, u)) return;
                var p = fetch(u, { cache: mode });
                p.catch(function () {});
                early[u] = { p: p, mode: mode };
            };
            if (!opts.fetchJson) ['/ru-stress.json', '/ru-stress-homographs.json', '/ru-stress-supplement.json'].forEach(function (f) { go(base + f, 'force-cache'); });
            ['curatedUrl', 'communityUrl', 'namesUrl', 'accentorUrl', 'yoLayerUrl', 'yoModelUrl', 'errataUrl', 'additionsUrl', 'normUrl', 'orderUrl']
                .forEach(function (k) { go(srcUrl(opts, k), 'no-cache'); });
        }
        // словарь eyo едет параллельно с большим словарём (см. YO в шапке)
        var yoP = opts.yoDict ? Promise.resolve(opts.yoDict)
            : loadYoDict(srcUrl(opts, 'yoUrl')).catch(function () { return null; });
        // основной словарь — байтами прямо в упакованную таблицу (scanDict), если его
        // не отдаёт свой fetchJson (тесты); необычный файл — JSON.parse, как раньше
        var s = null, stressUrl = base + '/ru-stress.json';
        if (!opts.fetchJson) {
            var raw;
            if (bytes && Object.prototype.hasOwnProperty.call(bytes, stressUrl)) {
                if (!bytes[stressUrl]) throw new Error('fetch ' + stressUrl);
                raw = bytes[stressUrl];
            } else {
                var sr = await get(stressUrl, 'force-cache');
                if (!sr.ok) throw new Error('fetch ' + stressUrl + ' ' + sr.status);
                raw = await sr.arrayBuffer();
            }
            var packedBase = scanDict(new Uint8Array(raw));
            s = packedBase ? new Layered(new Packed(packedBase)) : JSON.parse(new TextDecoder('utf-8').decode(raw));
        } else {
            s = await fetchJson(stressUrl);
        }
        var h = await fetchJson(base + '/ru-stress-homographs.json').catch(function () { return {}; });
        var homoDropped = sanitizeHomographs(s, h);
        // the flat reading of a homograph — the accentor's, before the people's
        // defaults below move theirs over it (see applyOrder)
        var orderCount = 0;
        try {
            var orr = await get(srcUrl(opts, 'orderUrl'), 'no-cache');
            orderCount = applyOrder(orr.ok ? await orr.json() : null, h);
        } catch (_) { /* без слоя — порядок словаря, как до 22.10.2026 */ }
        // optional supplement (AI-built, 2-pass-verified). ADDITIVE only: merged for keys
        // NOT already in the curated dict, so it never overrides ru-accent's own data.
        var sup = await fetchJson(base + '/ru-stress-supplement.json').catch(function () { return null; });
        if (sup && typeof sup === 'object') {
            for (var sk in sup) {
                if (Object.prototype.hasOwnProperty.call(sup, sk) && !mapHas(s, sk)) mapPut(s, sk, sup[sk]);
            }
        }
        // Curated misses (2026-08-07) — the HUMAN half of the pair with the
        // community overlay below: words the CC-BY-SA dict simply does not have
        // (`ва́ш`/`на́ш` paradigms, `бу́дешь`, `онла́йн`, `эквала́йзер`, `по́дкаст`,
        // `метада́нные`, `протанопи́я`…). These lived for three weeks in
        // services/silero-tts/ru-overrides.json, which only the server lane
        // reads, so the same words stayed unstressed in the browser. One file
        // now feeds both lanes; the server file still loads after it and wins,
        // because its respellings (синтез→синтэз) must never reach a textarea.
        // ADDITIVE only, and a value that respells the word is refused outright.
        var curatedCount = 0;
        try {
            var kr = await get(srcUrl(opts, 'curatedUrl'), 'no-cache');
            var kj = kr.ok ? await kr.json() : null;
            var kw = (kj && kj.words) || {};
            for (var ck in kw) {
                if (!Object.prototype.hasOwnProperty.call(kw, ck)) continue;
                if (mapHas(s, ck) || Object.prototype.hasOwnProperty.call(h || {}, ck)) continue;
                var cv = cleanForm(kw[ck]);
                if (!cv || foldKey(cv) !== foldKey(ck)) continue;   // respelling → not ours
                mapPut(s, ck, cv); curatedCount++;
            }
        } catch (_) { /* curated list is optional — never block the dictionaries */ }
        // Community overlay (data flywheel, 2026-07-15) — built by
        // `tts:stress-harvest` from users' own homograph choices and manual
        // stresses, behind anti-poison thresholds. Two sections:
        //   words:    NEW entries (dictionary misses users stressed) — ADDITIVE
        //             only, never overrides curated single OR homograph data;
        //   defaults: per-homograph community-preferred form — moved to
        //             forms[0], so {homograph:'first'} (the Supertonic guess
        //             path) and the review panel's first option follow real
        //             usage instead of dictionary order.
        // Lives in git-tracked /data/lang (deploys with the repo, unlike the
        // rsync-synced /vendor dicts). no-cache: the file CHANGES on harvests,
        // while the big dicts above are immutable-cached.
        var communityStats = { words: 0, defaults: 0 };
        var cUrl = srcUrl(opts, 'communityUrl');
        try {
            var cr = await get(cUrl, 'no-cache');
            var c = cr.ok ? await cr.json() : null;
            if (c && typeof c === 'object') {
                var cw = c.words || {}, cd = c.defaults || {};
                for (var wk in cw) {
                    if (Object.prototype.hasOwnProperty.call(cw, wk)
                        && !mapHas(s, wk)
                        && !Object.prototype.hasOwnProperty.call(h || {}, wk)) {
                        mapPut(s, wk, cw[wk]); communityStats.words++;
                    }
                }
                for (var dk in cd) {
                    if (!Object.prototype.hasOwnProperty.call(cd, dk)) continue;
                    var forms = (h || {})[dk];
                    if (!forms || !forms.length) continue;
                    var ix = forms.indexOf(cd[dk]);
                    if (ix > 0) { forms.splice(ix, 1); forms.unshift(cd[dk]); communityStats.defaults++; }
                    else if (ix === 0) communityStats.defaults++;
                    // unknown form string → ignore (stale/poisoned entry can't corrupt)
                }
            }
        } catch (_) { /* overlay is optional — never block the dictionaries */ }
        // Имена — свой файл в /data/lang (едет с репозиторием, как curated и
        // community, в отличие от больших словарей в /vendor). Проверяется
        // каждая запись: форма обязана быть ТЕМ ЖЕ словом со знаком, иначе это
        // переписывание произношения, а не ударение, и мы его не берём.
        var namesCount = 0, namesOvr = 0;
        try {
            var nr = await get(srcUrl(opts, 'namesUrl'), 'no-cache');
            var nj = nr.ok ? await nr.json() : null;
            if (nj && typeof nj === 'object') {
                NAMES = {}; NAMES_OVR = {};
                var takeNames = function (src, dst) {
                    var n = 0;
                    for (var k in (src || {})) {
                        if (!Object.prototype.hasOwnProperty.call(src, k)) continue;
                        var v = cleanForm(src[k]);
                        if (!v || foldKey(v) !== foldKey(k)) continue;
                        dst[k] = v; n++;
                    }
                    return n;
                };
                namesCount = takeNames(nj.names, NAMES);
                namesOvr = takeNames(nj.override, NAMES_OVR);
            }
        } catch (_) { /* имена опциональны — словарь без них работает как раньше */ }
        // Машинный слой — ПОСЛЕДНИМ: берётся только то, чего не знает ни один
        // слой, сделанный людьми. Слова — в GUESS (lookup() их не видит, см.
        // шапку); омографы акцентора — в общий список омографов, чтобы их решали
        // сервер по предложению и панель выбора, как любые другие.
        var guessCount = 0, guessHomo = 0;
        try {
            var gr = await get(srcUrl(opts, 'accentorUrl'), 'no-cache');
            var gj = gr.ok ? await gr.json() : null;
            if (gj && typeof gj === 'object') {
                var own = function (o, k) { return !!o && Object.prototype.hasOwnProperty.call(o, k); };
                var hh = h || {};
                GUESS = {};
                var gw = gj.words || {};
                for (var gk in gw) {
                    if (!own(gw, gk) || mapHas(s, gk) || own(hh, gk)) continue;
                    var gv = cleanForm(gw[gk]);
                    if (!gv || foldKey(gv) !== gk || countVowels(gv) < 2) continue;
                    GUESS[gk] = gv; guessCount++;
                }
                var gh = gj.homographs || {};
                for (var hk in gh) {
                    if (!own(gh, hk) || mapHas(s, hk) || own(hh, hk) || !Array.isArray(gh[hk])) continue;
                    var hf = gh[hk].map(cleanForm).filter(function (f) { return f && foldKey(f) === hk; });
                    if (hf.length > 1) { hh[hk] = hf; guessHomo++; }
                }
                h = hh;
            }
        } catch (_) { /* слой опционален — без него всё работает как раньше */ }
        // «ё» — после людских слоёв и акцентора, до нормы (норма перебивает и её).
        var yoCount = 0;
        try {
            var yr = await get(srcUrl(opts, 'yoLayerUrl'), 'no-cache');
            var yl = takeYoLayer(yr.ok ? await yr.json() : null);
            h = h || {};
            applyYoLayer(yl, s, h, GUESS);
            YO_SKIP = yl.skip; yoCount = yl.count;
        } catch (_) { /* без слоя спорные слова читает словарь — как до 01.10.2026 */ }
        var yoModelCount = 0;
        try {
            var ymr = await get(srcUrl(opts, 'yoModelUrl'), 'no-cache');
            var ym = takeYoModel(ymr.ok ? await ymr.json() : null);
            YO_CTX = ym.map; yoModelCount = ym.count;
        } catch (_) { /* без списка «все» и «звезды» читает словарь — как до 12.10.2026 */ }
        YO = await yoP;
        // Исправления словаря — после людских слоёв и акцентора, до нормы.
        var errataCount = 0;
        try {
            // `add` — свой файл: лёгкому пути (только омографы) 11 тыс. новых слов не нужны
            var both = await Promise.all([
                get(srcUrl(opts, 'errataUrl'), 'no-cache'),
                get(srcUrl(opts, 'additionsUrl'), 'no-cache')
                    .catch(function () { return null; })
            ]);
            var ej = both[0].ok ? await both[0].json() : null;
            var aj = both[1] && both[1].ok ? await both[1].json() : null;
            if (ej && aj) ej.add = aj.add;
            var et = takeErrata(ej);
            h = h || {};
            YO_SKIP = YO_SKIP || {};
            applyErrata(et, s, h, GUESS, YO_SKIP);
            errataCount = et.count;
        } catch (_) { /* без исправлений работает прежний словарь — как до 16.10.2026 */ }
        // Норма — после всех слоёв и поверх них (см. NORM в шапке).
        var normCount = 0;
        try {
            var nmr = await get(srcUrl(opts, 'normUrl'), 'no-cache');
            var nmj = nmr.ok ? await nmr.json() : null;
            var took = takeNorm(nmj);
            NORM = took.map; normCount = took.count;
            h = h || {};
            applyNorm(NORM, h, GUESS);
        } catch (_) { /* без нормы работает прежний словарь — как до 30.09.2026 */ }
        SINGLE = s; HOMO = h; ready = true;
        return { single: mapSize(s), homographs: Object.keys(h || {}).length, names: namesCount, namesOverride: namesOvr, community: communityStats, curated: curatedCount, homoDropped: homoDropped, accentor: guessCount, accentorHomographs: guessHomo, norm: normCount, yo: yoCount, yoDict: !!YO, yoModel: yoModelCount, errata: errataCount, order: orderCount };
    }

    // ── Полная загрузка: разбор в воркере, на странице — только поиск ──────
    // Замер 16.10.2026 (CPU×4, Chromium): loadMain на главном потоке — паузы до
    // 1,8 с и 4 с блокировки суммарно (скрипт eyo ~1 с, JSON.parse 25 МБ ~1,3 с,
    // слой акцентора ~0,45 с), +115 МБ памяти страницы. Здесь тот же loadMain
    // идёт в воркере, собранном из текста этой же фабрики, а странице приходят
    // упакованные таблицы (packMap) переносом буферов. Нет Worker/Blob, свой
    // `fetchJson` (тесты) или `worker:false` — прежний путь; воркер отказал —
    // тоже прежний путь, с пометкой в телеметрии.
    function exportTables(stats) {
        var yo = null;
        if (YO && !(YO instanceof Packed)) {
            // поиск eyo идёт только по ключу в нижнем регистре (yoForm)
            yo = {};
            for (var k in YO) if (Object.prototype.hasOwnProperty.call(YO, k) && k === k.toLowerCase()) yo[k] = YO[k];
        }
        var pk = function (m) { return !m ? null : (m instanceof Packed ? m : packMap(m)); };
        // словарь из scanDict: основа уходит как есть, правки слоёв пакуются отдельно
        var lay = SINGLE instanceof Layered ? SINGLE : null;
        // мелкие слои — одной строкой JSON: structured clone разворачивает 6,4 тыс.
        // массивов омографов на главном потоке при чтении ev.data (замер: 130–170 мс
        // при CPU×4), а JSON.parse той же строки — в разы быстрее
        var homo = null;
        if (HOMO && !(HOMO instanceof Packed)) {
            homo = {};
            for (var hk in HOMO) if (Object.prototype.hasOwnProperty.call(HOMO, hk)) homo[hk] = [].concat(HOMO[hk]).join(HOMO_SEP);
        }
        return { ok: 1, single: lay ? null : pk(SINGLE), singleBase: lay ? lay.base : null, singleOver: lay ? pk(lay.over) : null,
            guess: pk(GUESS), yo: yo ? packMap(yo) : pk(YO), homo: homo ? packMap(homo) : pk(HOMO),
            side: JSON.stringify({ stats: stats, singleDead: lay ? lay.dead : null, names: NAMES, namesOvr: NAMES_OVR, norm: NORM, yoSkip: YO_SKIP, yoCtx: YO_CTX }) };
    }
    function transferList(msg) {
        var out = [];
        [msg.single, msg.singleBase, msg.singleOver, msg.guess, msg.yo, msg.homo].forEach(function (p) { if (p) out.push(p.bytes.buffer, p.ko.buffer, p.vo.buffer, p.tab.buffer); });
        return out;
    }
    function adopt(msg) {
        var side = typeof msg.side === 'string' ? JSON.parse(msg.side) : msg;
        SINGLE = msg.singleBase ? new Layered(new Packed(msg.singleBase), msg.singleOver ? new Packed(msg.singleOver) : {}, side.singleDead || {})
            : msg.single ? new Packed(msg.single) : {};
        GUESS = msg.guess ? new Packed(msg.guess) : null;
        YO = msg.yo ? new Packed(msg.yo) : null;
        HOMO = msg.homo ? new Packed(msg.homo) : (side.homo || {}); homoSplit = null;
        NAMES = side.names || null; NAMES_OVR = side.namesOvr || null;
        NORM = side.norm || null; YO_SKIP = side.yoSkip || null; YO_CTX = side.yoCtx || null;
        ready = true;
        return side.stats;
    }

    function workerUsable(opts) {
        return !!SELF && opts.worker !== false && !opts.fetchJson && !opts.yoDict
            && typeof Worker === 'function' && typeof Blob === 'function'
            && typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function'
            && typeof location !== 'undefined';
    }

    function loadViaWorker(opts) {
        var o = {}, k;
        for (k in SRC) if (Object.prototype.hasOwnProperty.call(SRC, k)) o[k] = new URL(srcUrl(opts, k), location.href).href;
        // `fetchBytes` — сеть страницы (докачка, прогресс): три файла словаря
        // скачивает она, разбирает воркер (буферы уходят переносом)
        var big = typeof opts.fetchBytes === 'function'
            ? ['/ru-stress.json', '/ru-stress-homographs.json', '/ru-stress-supplement.json'].map(function (f) { return o.base + f; }) : [];
        return Promise.all(big.map(function (u, i) {
            return Promise.resolve().then(function () { return opts.fetchBytes(u); })
                .then(function (b) { return [u, b]; }, function (e) { if (i === 0) throw e; return [u, null]; });
        })).then(function (pairs) {
            return new Promise(function (resolve, reject) {
                var url = URL.createObjectURL(new Blob(['self.__RA_WORKER=1;(' + SELF.toString() + ')(null);'], { type: 'text/javascript' }));
                var w, done = false;
                var finish = function (err, msg) {
                    if (done) return; done = true;
                    try { w.terminate(); } catch (_) {}
                    URL.revokeObjectURL(url);
                    if (err) reject(err); else resolve(adopt(msg));
                };
                try { w = new Worker(url); } catch (e) { URL.revokeObjectURL(url); reject(e); return; }
                w.onmessage = function (ev) {
                    var m = ev.data || {};
                    if (m.ok) finish(null, m); else finish(new Error(m.error || 'ru-accent worker failed'));
                };
                w.onerror = function (ev) {
                    if (ev && ev.preventDefault) ev.preventDefault();
                    finish(new Error('ru-accent worker: ' + ((ev && ev.message) || 'error')));
                };
                var bytes = {}, tr = [];
                pairs.forEach(function (p) { bytes[p[0]] = p[1]; if (p[1]) tr.push(p[1]); });
                w.postMessage({ cmd: 'load', opts: o, bytes: bytes }, tr);
            });
        }).then(function (stats) {
            // Скрипт eyo воркеру не дался (importScripts под чужим CSP, сбой сети) — страница
            // грузит его сама, как до воркера: без него «еще», «идет» остались бы без ударения.
            if (!stats || stats.yoDict !== false || typeof document === 'undefined') return stats;
            return loadYoDict(srcUrl(opts, 'yoUrl')).then(function (d) {
                if (d) { YO = d; stats.yoDict = true; }
                return stats;
            }, function () { return stats; });
        });
    }

    // Прежний путь со своей сетью страницы: `fetchBytes` становится `fetchJson` трёх файлов словаря.
    function mainOpts(opts) {
        if (opts.fetchJson || typeof opts.fetchBytes !== 'function') return opts;
        var o = {};
        for (var k in opts) if (Object.prototype.hasOwnProperty.call(opts, k)) o[k] = opts[k];
        o.fetchJson = function (u) {
            return Promise.resolve(opts.fetchBytes(u)).then(function (b) { return JSON.parse(new TextDecoder('utf-8').decode(b)); });
        };
        return o;
    }

    var loading = null;
    function load(opts) {
        opts = opts || {};
        if (loading) return loading;
        var p = workerUsable(opts)
            ? loadViaWorker(opts).catch(function (e) {
                try { if (typeof window !== 'undefined' && window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'ru_accent_worker_fallback', detail: String((e && e.message) || e).slice(0, 160) }); } catch (_) {}
                return loadMain(mainOpts(opts));
            })
            : loadMain(mainOpts(opts));
        loading = p;
        var clear = function () { loading = null; };
        p.then(clear, clear);
        return p;
    }

    // Воркер разбора: тот же loadMain, ответ — упакованные таблицы.
    if (IN_WORKER) {
        self.onmessage = function (ev) {
            var m = ev.data || {};
            if (m.cmd !== 'load') return;
            var o = m.opts || {};
            o.bytes = m.bytes || null;
            loadMain(o).then(function (stats) {
                var msg = exportTables(stats);
                self.postMessage(msg, transferList(msg));
            }, function (e) {
                self.postMessage({ ok: 0, error: String((e && e.message) || e) });
            });
        };
    }

    // Node test helper: load dict from local files synchronously. `normJson` —
    // the parsed ru-stress-norm.json, run through the same checks as load();
    // `yoDict` — eyo's dictionary ({е-ключ: ё-форма}), `yoLayerJson` — the parsed
    // ru-stress-yo.json, `yoModelJson` — the parsed ru-stress-yo-model.json.
    function loadFromObject(single, homo, names, namesOvr, guesses, normJson, yoDict, yoLayerJson, yoModelJson, errataJson) {
        SINGLE = single; HOMO = homo || {}; NAMES = names || null; NAMES_OVR = namesOvr || null; GUESS = guesses || null;
        YO = yoDict || null; YO_SKIP = null;
        YO_CTX = yoModelJson ? takeYoModel(yoModelJson).map : null;
        if (yoLayerJson) { var yl = takeYoLayer(yoLayerJson); applyYoLayer(yl, SINGLE, HOMO, GUESS); YO_SKIP = yl.skip; }
        if (errataJson) { YO_SKIP = YO_SKIP || {}; applyErrata(takeErrata(errataJson), SINGLE, HOMO, GUESS, YO_SKIP); }
        NORM = normJson ? takeNorm(normJson).map : null;
        if (NORM) applyNorm(NORM, HOMO, GUESS);
        ready = true;
    }

    // ── Context picks — the reading a homograph takes IN ITS SENTENCE ──
    // A flat reading per word is wrong by construction the moment the other
    // meaning shows up: «я е́ду домой» / «купи еду́», «не имею пра́ва» / «ты
    // права́». Measured 2026-09-16 on sentences using both readings: dictionary
    // order 53 %, the context model (silero-stress, inside the Silero daemon)
    // 93–97 %. The page cannot run that model (no browser build), so it asks
    // the server — which answers exactly what Silero voices, so a panel never
    // suggests one reading while the voice speaks another.
    // Fail-open by design: no server (intl has none), a timeout, an error, or a
    // reading this dictionary cannot place → no pick, and every caller keeps
    // dictionary order exactly as before. After a failure the page stops asking
    // for a minute instead of retrying on every keystroke.
    var PICK_URL = '/api/stress-homographs';
    var PICK_TIMEOUT_MS = 2500;
    var PICK_MAX_CHARS = 16000;            // the daemon's own ceiling
    var PICK_BACKOFF_MS = 60000;
    var pickCache = [];                    // [{text, picks}], most recent last
    var pickInflight = {};
    var pickDownUntil = 0;
    var MARKED_WORD = /[А-Яа-яЁё́̀+]+/g;
    var LETTER_OR_MARK = /[А-Яа-яЁё́̀+]/;

    // Ordinal of the stressed vowel — the main one (see mainVowel): a reading read by
    // its ё («сёстры», «сохранён») carries no acute and used to count as unstressed,
    // so the server's pick for it was dropped as invalid. -1 if none.
    function stressVowel(form) {
        return mainVowel(form);
    }
    function sameReading(a, b) {
        var at = stressVowel(a);
        return at >= 0 && at === stressVowel(b) && foldKey(a) === foldKey(b);
    }

    // An unmarked word typed without ё whose ё the sentence decides (YO_CTX).
    function isYoContextWord(word) {
        return !!YO_CTX && !/[ёЁ]/.test(word) && YO_CTX[foldKey(word)] === 1;
    }

    function hasOpenHomograph(text) {
        var re = new RegExp(MARKED_WORD.source, 'g'), m;
        while ((m = re.exec(text)) !== null) {
            if (/[+́̀]/.test(m[0])) continue;
            if (isYoContextWord(m[0])) return true;     // «все» — один слог, а спросить надо
            if (countVowels(m[0]) < 2) continue;
            if (lookup(m[0]).type === 'homograph') return true;
        }
        return false;
    }

    // A pick is used only if it still describes THIS text: a whole, unmarked
    // word that this dictionary knows as a homograph, stressed on a vowel — or
    // a YO_CTX word read WITH ё («всё», «звёзды»): the server answers those only
    // when the sentence wants ё, and nothing here can read them so.
    function validPick(text, p) {
        if (!p || typeof p.start !== 'number' || typeof p.end !== 'number' || typeof p.form !== 'string') return false;
        if (p.start < 0 || p.end > text.length || p.start >= p.end) return false;
        var slice = text.slice(p.start, p.end);
        if (!/^[А-Яа-яЁё]+$/.test(slice)) return false;
        if (LETTER_OR_MARK.test(text.charAt(p.start - 1)) || LETTER_OR_MARK.test(text.charAt(p.end))) return false;
        if (stressVowel(p.form) < 0 || foldKey(slice) !== foldKey(cleanForm(p.form))) return false;
        if (lookup(slice).type === 'homograph') return true;
        return isYoContextWord(slice) && /[ёЁ]/.test(p.form);
    }

    function contextPicks(text, opts) {
        opts = opts || {};
        text = String(text == null ? '' : text);
        if (!ready || !text || text.length > PICK_MAX_CHARS || Date.now() < pickDownUntil || !hasOpenHomograph(text)) {
            return Promise.resolve([]);
        }
        for (var c = 0; c < pickCache.length; c++) {
            if (pickCache[c].text === text) return Promise.resolve(pickCache[c].picks.slice());
        }
        if (pickInflight[text]) return pickInflight[text].then(function (p) { return p.slice(); });
        var doFetch = opts.fetch || (typeof fetch === 'function' ? fetch : null);
        if (!doFetch) return Promise.resolve([]);

        var headers = { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
        try {
            var meta = document.querySelector('meta[name="csrf-token"]');
            if (meta && meta.content) headers['X-CSRF-TOKEN'] = meta.content;
        } catch (_) { /* no document (tests) */ }
        var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
        var timer = null;
        var req = new Promise(function (resolve) {
            timer = setTimeout(function () { if (ctrl) ctrl.abort(); resolve(null); }, opts.timeout || PICK_TIMEOUT_MS);
            Promise.resolve().then(function () {
                return doFetch(opts.url || PICK_URL, {
                    method: 'POST', credentials: 'same-origin', headers: headers,
                    body: JSON.stringify({ text: text }), signal: ctrl ? ctrl.signal : undefined
                });
            }).then(function (r) { return r && r.ok ? r.json() : null; })
              .then(resolve, function () { resolve(null); });
        }).then(function (data) {
            clearTimeout(timer);
            delete pickInflight[text];
            if (!data || !Array.isArray(data.picks)) { pickDownUntil = Date.now() + PICK_BACKOFF_MS; return []; }
            var picks = [];
            for (var i = 0; i < data.picks.length; i++) {
                var p = data.picks[i];
                if (validPick(text, p)) picks.push({ start: p.start, end: p.end, form: cleanForm(p.form) });
            }
            pickCache.push({ text: text, picks: picks });
            if (pickCache.length > 8) pickCache.shift();
            return picks;
        });
        pickInflight[text] = req;
        return req.then(function (p) { return p.slice(); });
    }

    // Plant the picks into the text (the Supertonic path): marked words are
    // then left alone by accentuate(), exactly like a user's own mark.
    function applyPicks(text, picks) {
        text = String(text);
        var list = (picks || []).filter(function (p) { return validPick(text, p); })
            .sort(function (a, b) { return b.start - a.start; });
        for (var i = 0; i < list.length; i++) {
            var p = list[i];
            text = text.slice(0, p.start) + applyCase(text.slice(p.start, p.end), p.form) + text.slice(p.end);
        }
        return text;
    }

    function pickAt(picks, start) {
        for (var i = 0; picks && i < picks.length; i++) if (picks[i].start === start) return picks[i].form;
        return null;
    }

    // The same readings with the suggested one first. A reading the model knows
    // and this dictionary does not («здоро́во» — питаться здоро́во) is added.
    function withSuggestion(forms, form) {
        var out = (forms || []).slice();
        if (!form) return out;
        for (var i = 0; i < out.length; i++) {
            if (sameReading(out[i], form)) { var hit = out.splice(i, 1)[0]; out.unshift(hit); return out; }
        }
        out.unshift(cleanForm(form));
        return out;
    }

    // ── Куда встаёт знак, когда человек нажал кнопку «Поставить ударение» ──
    //
    // Замер на проде 22.09.2026: кнопка вставляла U+0301 РОВНО в позицию
    // каретки, поэтому три жеста из четырёх молча давали мусор, а не ударение:
    //
    //   двойной клик по слову «номера» → « ́номера»  (акут на ПРОБЕЛЕ)
    //   каретка перед гласной (привычка «+а» у Яндекса) → «номер́а» (акут на «р»)
    //   нажатие без фокуса в поле → « ́Все …» (акут в начале текста)
    //   каретка сразу ПОСЛЕ гласной → «номера́» ← единственный верный жест
    //
    // Ни один из трёх промахов не виден глазом (комбинирующий знак садится на
    // соседнюю букву или на пробел) и ни один не доезжает до движка ударением —
    // отсюда «при добавлении ударения ничего не меняется» (#359), «выбрал
    // Дмитрия и не могу чтоб с ударением» (#461) и «как поставить ударение на
    // последнюю букву А» (#8225). Правило «переносить на БЛИЖАЙШУЮ гласную»
    // уже принято для «+» (18.09, см. accentuateWord) — здесь та же дверь для
    // кнопки: знак принадлежит СЛОВУ, а не смещению в строке.
    //
    // Словарь тут не обязателен: без него порядок гласных — слева направо, с
    // ним первыми идут чтения словаря. Кнопка обязана срабатывать мгновенно, а
    // данные весят 3.6 МБ и грузятся секундами.
    var GRAVE_MARK = '̀';
    var YO_RE = /[ёЁ]/;
    function isVowelCh(ch) { return !!ch && VOWELS.indexOf(ch.toLowerCase()) >= 0; }
    function isCyrCh(ch) { return !!ch && CYR.test(ch); }
    function isMarkCh(ch) { return ch === ACUTE || ch === GRAVE_MARK || ch === '+'; }

    /** Позиции гласных в слове БЕЗ знаков. «ё» пропускаем — она ударна сама. */
    function vowelSlots(plain) {
        var out = [];
        for (var i = 0; i < plain.length; i++) {
            if (!isVowelCh(plain[i]) || YO_RE.test(plain[i])) continue;
            out.push(i);
        }
        return out;
    }

    /** Ударная гласная словарной формы — в индексах слова БЕЗ знаков. */
    function vowelSlotOfForm(form) {
        var plain = 0;
        for (var i = 0; i < form.length; i++) {
            var ch = form[i];
            if (ch === '+') return plain;                                   // «+» стоит ПЕРЕД гласной
            if (ch === ACUTE || ch === GRAVE_MARK) return plain - 1;        // U+0301 — ПОСЛЕ неё
            plain++;
        }
        return -1;
    }

    /**
     * placeMark(text, selStart, selEnd, opts) → {text, cursor, applied, reason, word, slot}
     *
     * reason: 'moved' | 'removed' | 'no_word' | 'not_focused' | 'already_stressed'
     * opts.focused === false — поле не в фокусе, каретка 0/0 ничего не значит.
     */
    function placeMark(text, selStart, selEnd, opts) {
        opts = opts || {};
        text = String(text == null ? '' : text);
        // Поле без фокуса — каретка 0/0 ничего не значит, и гадать нельзя:
        // раньше отсюда рождался акут в самом начале текста.
        if (opts.focused === false) {
            return { text: text, cursor: selStart | 0, applied: false, reason: 'not_focused', word: '', slot: -1 };
        }
        var a = Math.max(0, Math.min(text.length, selStart | 0));
        var b = Math.max(0, Math.min(text.length, selEnd == null ? a : (selEnd | 0)));
        if (b < a) { var swap = a; a = b; b = swap; }
        var noSel = a === b;

        // 1. Снятие знака рядом с кареткой — поведение кнопки, которое было и
        //    остаётся: нажал рядом со своим знаком → убрал.
        if (noSel) {
            var at = -1;
            if (text[a - 1] === ACUTE || text[a - 1] === GRAVE_MARK) at = a - 1;
            else if (text[a] === ACUTE || text[a] === GRAVE_MARK) at = a;
            if (at >= 0) {
                return { text: text.slice(0, at) + text.slice(at + 1), cursor: at < a ? a - 1 : a,
                         applied: true, reason: 'removed', word: '', slot: -1 };
            }
        }

        // 2. Слово под кареткой/выделением. Дефис в слово НЕ входит: «кто-то» —
        //    два слова, и частица после дефиса безударна (см. ENCLITICS).
        var probe = -1;
        if (isCyrCh(text[a]) || isMarkCh(text[a])) probe = a;
        else if (a > 0 && (isCyrCh(text[a - 1]) || isMarkCh(text[a - 1]))) probe = a - 1;
        else { for (var k = a; k < b; k++) { if (isCyrCh(text[k])) { probe = k; break; } } }
        if (probe < 0) {
            return { text: text, cursor: a, applied: false,
                     reason: opts.focused === false ? 'not_focused' : 'no_word', word: '', slot: -1 };
        }
        var ws = probe; while (ws > 0 && (isCyrCh(text[ws - 1]) || isMarkCh(text[ws - 1]))) ws--;
        var we = probe; while (we < text.length && (isCyrCh(text[we]) || isMarkCh(text[we]))) we++;
        var raw = text.slice(ws, we);
        var plain = raw.replace(/[+̀́]/g, '');
        if (!plain) return { text: text, cursor: a, applied: false, reason: 'no_word', word: '', slot: -1 };

        // «ё» ударна сама по себе — у слова с ней ударение уже определено, и
        // второй знак сделал бы в одном слове два (тот же запрет, что в
        // accentuateWord: «шё́пот» уехал так в модель клона, #6850).
        if (YO_RE.test(plain)) {
            return { text: text, cursor: a, applied: false, reason: 'already_stressed', word: plain, slot: -1 };
        }
        var slots = vowelSlots(plain);
        if (!slots.length) {
            return { text: text, cursor: a, applied: false, reason: 'no_word', word: plain, slot: -1 };
        }

        // 3. Где знак стоит сейчас — в индексах БЕЗ знаков.
        var curSlot = -1, plainIdx = 0;
        for (var i2 = 0; i2 < raw.length; i2++) {
            var ch2 = raw[i2];
            if (ch2 === '+') { curSlot = plainIdx; continue; }
            if (ch2 === ACUTE || ch2 === GRAVE_MARK) { curSlot = plainIdx - 1; continue; }
            plainIdx++;
        }

        var slot;
        if (noSel && curSlot < 0) {
            // Каретка внутри слова: стоит сразу ПОСЛЕ гласной — она и есть;
            // иначе ближайшая гласная ВПЕРЁД (правило «+»), иначе последняя назад.
            var relPlain = 0;
            for (var j = 0; j < a - ws && j < raw.length; j++) if (!isMarkCh(raw[j])) relPlain++;
            if (relPlain > 0 && slots.indexOf(relPlain - 1) >= 0) slot = relPlain - 1;
            else {
                slot = -1;
                for (var s = 0; s < slots.length; s++) if (slots[s] >= relPlain) { slot = slots[s]; break; }
                if (slot < 0) slot = slots[slots.length - 1];
            }
        } else {
            // Слово выделено (двойной клик) или знак уже стоит → перебор гласных.
            // Порядок: сперва чтения словаря, потом остальные гласные.
            var order = [], seen = {};
            var look = lookup(plain);
            var forms = (look && look.forms) ? look.forms : [];
            if (!forms.length && guess(plain)) forms = [guess(plain)];   // перебор начинается с догадки
            for (var f = 0; f < forms.length; f++) {
                var vs = vowelSlotOfForm(cleanForm(forms[f]));
                if (vs >= 0 && slots.indexOf(vs) >= 0 && !seen[vs]) { seen[vs] = 1; order.push(vs); }
            }
            for (var s2 = 0; s2 < slots.length; s2++) if (!seen[slots[s2]]) { seen[slots[s2]] = 1; order.push(slots[s2]); }
            if (curSlot < 0) slot = order[0];
            else {
                var at2 = order.indexOf(curSlot);
                slot = order[(at2 < 0 ? 0 : at2 + 1) % order.length];
            }
        }

        var marked = plain.slice(0, slot + 1) + ACUTE + plain.slice(slot + 1);
        return { text: text.slice(0, ws) + marked + text.slice(we), cursor: ws + slot + 2,
                 applied: true, reason: 'moved', word: plain, slot: slot,
                 // границы слова ПОСЛЕ правки: выделение можно вернуть, и тогда
                 // следующее нажатие перебирает гласные, а не снимает знак
                 start: ws, end: ws + marked.length };
    }

    function resetPicks() { pickCache = []; pickInflight = {}; pickDownUntil = 0; }

    return {
        load: load, loadHomographs: loadHomographs, loadFromObject: loadFromObject,
        isReady: function () { return ready; },
        accentuate: accentuate, lookup: lookup, guess: guess, norm: norm, nameForm: nameForm, foldKey: foldKey, _applyCase: applyCase, lookupCompound: lookupCompound,
        yoWordForm: yoWordForm, mainVowel: mainVowel, _takeYoLayer: takeYoLayer, _takeErrata: takeErrata, _sameYo: sameYo, _mainOnly: mainOnly,
        _cleanForm: cleanForm, _sanitizeHomographs: sanitizeHomographs,
        _applyOrder: applyOrder, _oneMark: oneMark, _oneMarkForms: oneMarkForms, _applyCtxRules: applyCtxRules,
        contextPicks: contextPicks, applyPicks: applyPicks, pickAt: pickAt,
        withSuggestion: withSuggestion, sameReading: sameReading,
        placeMark: placeMark, _vowelSlots: vowelSlots, _vowelSlotOfForm: vowelSlotOfForm,
        _validPick: validPick, _resetPicks: resetPicks,
        // упакованные таблицы: тест сверяет их ответы с объектами на всём словаре
        _packMap: packMap, _scanDict: scanDict, _Packed: Packed, _exportTables: exportTables, _transferList: transferList, _adopt: adopt, _mGet: mGet, _mHas: mHas
    };
});
